import { useContext, useState } from 'react'
import {
  Button,
  Fa,
  Form,
  Modal,
  showErrorMessage,
  TextInput,
  Toggle,
} from '@eltoro-ui/components'
import { useMediaQuery } from '@eltoro-ui/hooks'
import { V1Subscription, V1Pixel, V1WebToHomeNoteType } from 'next-gen-sdk'
import { v4 as uuid } from 'uuid'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { ThemeContext, useAppContext } from 'Contexts'
import { createSubscription } from 'Requests'
import { PixelGeoLocationFilters } from './GeoLocationFilters'
import { IncludedTimeInput } from './IncludedTimeInput'
import { PixelNotes } from './PixelNotes'
import { RuleSets } from './RuleSets'
import { PixelSelect } from './PixelSelect'
import { PixelTagInput } from './PixelTagInput'
import { PixelFormItem } from './shared'
import { RuleSetWithTempId } from './types'
import './PixelSubscriptionCreate.scss'

export const PixelSubscriptionCreate = ({
  offClick,
  setRefresh,
  incomingPixel,
}: {
  offClick: () => void
  setRefresh?: React.Dispatch<React.SetStateAction<string>>
  incomingPixel?: Partial<V1Pixel>
}) => {
  const [pixel, setPixel] = useState<Partial<V1Pixel | undefined>>(
    incomingPixel,
  )
  const [subscription, setSubscription] = useState<Partial<V1Subscription>>()
  const [parentRuleSet, setParentRuleSet] = useState<
    Partial<RuleSetWithTempId>
  >({
    operator: 'and',
    rules: [{ term: '', tempId: uuid(), type: 'term' }],
    tempId: uuid(),
  })
  const [saving, setSaving] = useState(false)
  const [subscriptionTypes, setSubscriptionTypes] = useState<string[]>([
    'Web-to-Home',
  ])
  const [enableGeo, setEnableGeo] = useState(false)
  const [notes, setNotes] = useState<{
    content: string
    type: V1WebToHomeNoteType
  }>()
  const { currentOrg, webToHomeApi, isAdmin } = useAppContext()
  const nav = useNavigate()
  const query = currentOrg?.id ? `?org_id=${currentOrg.id}` : ''

  const setters = {
    setPixel,
    setParentRuleSet,
    setSubscription,
  }

  const disableSave =
    saving ||
    (!pixel?.id && !pixel?.name) ||
    !subscription?.name ||
    !subscriptionTypes.length

  const handleSave = async () => {
    if (!currentOrg?.id || !currentOrg?.name || !pixel || !subscription) return
    setSaving(true)
    try {
      await createSubscription(
        currentOrg.id,
        currentOrg.name,
        pixel,
        subscription,
        parentRuleSet,
      ).then(async (res) => {
        if (
          webToHomeApi &&
          res?.pixel.id &&
          res?.subscription.id &&
          res.subscription.orgId &&
          notes?.content
        ) {
          await webToHomeApi.advertisingPlatformServiceCreateSubscriptionNote(
            res.pixel.id,
            res?.subscription?.id,
            notes,
            res.subscription.orgId,
          )
        }
        offClick()
        if (res?.subscription?.id && res.subscription.pixelId)
          nav(
            `/pixel-library/pixel/${res.subscription.pixelId}/subscription/${res.subscription.id}${query}`,
          )
        if (setRefresh) setRefresh(uuid())
      })
    } catch (e) {
      showErrorMessage('Error saving pixel and subscription', '')
    } finally {
      setSaving(false)
    }
  }

  const isNameValid = (subscription?.name || '').length <= 100

  const [darkMode] = useMediaQuery('(prefers-color-scheme: dark)')
  const { name: theme } = useContext(ThemeContext)

  const themeMod =
    theme === 'darkBlue' ||
    theme === 'dark' ||
    (darkMode && theme === 'default')
      ? 'dark-mode'
      : ''

  return (
    <Modal
      className="PixelSubscriptionCreate tablet:min-w-[100%] min-w-[50%]"
      header="Create New Subscription"
      offClick={offClick}
    >
      <Form
        className={classNames(
          'PixelCreate__form flex flex-col gap-4 p-4',
          themeMod,
        )}
      >
        <PixelFormItem
          htmlFor="subscription-name"
          label="Subscription Name"
          required
          valid={isNameValid}
          counter
        >
          <TextInput
            value={subscription?.name || ''}
            id="subscription-name"
            onChange={(e) =>
              setSubscription((prev) => ({ ...prev, name: e.target.value }))
            }
            maxLength={101}
            minLength={1}
            valid={isNameValid}
            hideValidIcon
          />
        </PixelFormItem>
        <PixelFormItem
          htmlFor="pixel-tags"
          label={<span className="mb-4">Tags</span>}
        >
          <PixelTagInput
            tags={subscription?.tags || []}
            setTags={(tags) => setSubscription((prev) => ({ ...prev, tags }))}
          />
        </PixelFormItem>
        <PixelFormItem
          label="Subscription Types"
          htmlFor="subscription-types"
          required
        >
          <div className="flex gap-2">
            {['Web-to-Home', 'Digital Targeting'].map((type) => {
              const disabled = false
              return (
                <Button
                  key={type}
                  iconRight={
                    <Fa
                      icon={
                        subscriptionTypes.includes(type)
                          ? 'square-check'
                          : 'square'
                      }
                      size={1}
                      type={
                        !subscriptionTypes.includes(type) ? 'light' : undefined
                      }
                    />
                  }
                  className={classNames('rounded-s', {
                    'cursor-not-allowed': disabled,
                    '!bg-transparent': !subscriptionTypes.includes(type),
                    '!text-primary-500': !subscriptionTypes.includes(type),
                  })}
                  onClick={() => {
                    const isAdding = !subscriptionTypes.includes(type)
                    setSubscriptionTypes((prev) =>
                      isAdding
                        ? [...new Set([...prev, type])]
                        : prev.filter((t) => t !== type),
                    )
                    if (type === 'Digital Targeting') {
                      setSubscription((prev) => ({
                        ...prev,
                        retargetingEnabled: isAdding,
                        enabled: isAdding,
                      }))
                    }
                  }}
                  kind={
                    subscriptionTypes.includes(type) ? 'primary' : 'default'
                  }
                  disabled={
                    disabled || (!isAdmin && type === 'Digital Targeting')
                  }
                >
                  {type}
                </Button>
              )
            })}
          </div>
        </PixelFormItem>
        {subscriptionTypes.includes('Digital Targeting') && (
          <PixelFormItem
            label="Keep IP In Segment"
            htmlFor="included-time"
            required
          >
            <IncludedTimeInput
              onChange={(option) => {
                setSubscription((prev) => ({
                  ...prev,
                  includedTime: Number(option.value),
                }))
              }}
            />
          </PixelFormItem>
        )}
        <PixelNotes
          type="subscription"
          onChange={(notes, type) => setNotes({ content: notes, type })}
        />
        <PixelSelect
          pixel={pixel}
          disabled={incomingPixel !== undefined}
          {...setters}
        />
        <RuleSets parentRuleSet={parentRuleSet} {...setters} />
        <div className="flex flex-col gap-4">
          <Toggle
            label="Geographic Filters"
            onChange={setEnableGeo}
            className="max-w-max"
          />
          {enableGeo && (
            <PixelGeoLocationFilters
              zipsFiltered={subscription?.zipsFiltered || []}
              statesFiltered={subscription?.statesFiltered || []}
              zipsIncluded={subscription?.zipsIncluded || []}
              statesIncluded={subscription?.statesIncluded || []}
              handleZipsFiltered={(zips) =>
                setSubscription((prev) => ({ ...prev, zipsFiltered: zips }))
              }
              handleStatesFiltered={(states) =>
                setSubscription((prev) => ({ ...prev, statesFiltered: states }))
              }
              handleZipsIncluded={(zips) =>
                setSubscription((prev) => ({ ...prev, zipsIncluded: zips }))
              }
              handleStatesIncluded={(states) =>
                setSubscription((prev) => ({ ...prev, statesIncluded: states }))
              }
            />
          )}
        </div>
        <Toggle
          label="Enable Daily Processing"
          isChecked={subscription?.enabled || false}
          isDisabled={subscriptionTypes.includes('Digital Targeting')}
          onChange={(enabled) =>
            setSubscription((prev) => ({ ...prev, enabled }))
          }
          className="max-w-max"
        />
        {subscriptionTypes.includes('Digital Targeting') && (
          <small>
            NOTE: Daily Processing is enabled automatically on Digital Targeting
            subscriptions
          </small>
        )}
        <div className="PixelCreate__form-buttons flex justify-end gap-2">
          <Button onClick={() => offClick()} disabled={saving}>
            Cancel
          </Button>
          <Button
            iconLeft={
              saving ? <Fa icon="spinner" size={1} animate="spin" /> : undefined
            }
            kind="primary"
            disabled={disableSave}
            onClick={handleSave}
          >
            Create Subscription
          </Button>
        </div>
      </Form>
    </Modal>
  )
}
